import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { WidgetRoot } from '../components/widget-root';
import type { WidgetConfig, WidgetUiState } from '../types';

export const HOST_ID = 'orderpilot-root';

export type WidgetDom = WidgetUiState & {
    bubble: HTMLButtonElement;
    unmount: () => void;
};

let widgetStyles: string | undefined;

export function setWidgetStyles(css: string): void {
    widgetStyles = css;
}

export function mount(doc: Document, config: WidgetConfig): WidgetDom {
    const win = doc.defaultView ?? window;
    const host = doc.createElement('div');
    host.id = HOST_ID;
    host.setAttribute('style', 'display:block!important;');
    doc.body.append(host);

    const shadow = host.attachShadow({ mode: 'open' });
    injectStyles(shadow);

    const panelContainer = doc.createElement('div');
    panelContainer.className = 'panel';
    panelContainer.setAttribute('data-open', 'false');

    const bubble = doc.createElement('button');
    bubble.type = 'button';
    bubble.className = 'bubble';
    bubble.setAttribute('aria-label', 'Open chat');
    bubble.setAttribute('data-tip', 'Chat with us');
    bubble.innerHTML = MESSAGE_CIRCLE_ICON;

    const badge = doc.createElement('span');
    badge.className = 'bubble-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.hidden = true;
    bubble.append(badge);

    const setUnread = (count: number) => {
        const safe = Math.max(0, Math.min(99, count));
        badge.hidden = safe === 0;
        badge.textContent = safe > 9 ? '9+' : String(safe);
        bubble.setAttribute(
            'aria-label',
            safe > 0 ? `Open chat, ${safe} new messages` : 'Open chat',
        );
    };

    shadow.append(panelContainer, bubble);

    let reactRoot: Root | null = null;

    const renderReact = (open: boolean) => {
        if (!reactRoot) {
            reactRoot = createRoot(panelContainer);
        }

        reactRoot.render(
            <WidgetRoot
                widgetKey={config.widgetKey}
                apiOrigin={config.apiOrigin}
                open={open}
                onClose={() => {
                    setPanelOpen(false);
                    bubble.focus();
                }}
                onUnread={(count) => {
                    if (!isOpen()) {
                        setUnread(count);
                    }
                }}
            />,
        );
    };

    const syncSheetHeight = () => {
        const vv = win.visualViewport;

        if (!vv || !win.matchMedia('(max-width: 480px)').matches) {
            panelContainer.style.height = '';

            return;
        }

        panelContainer.style.height = `${Math.max(280, Math.min(600, vv.height - 110))}px`;
    };

    const onViewportResize = () => syncSheetHeight();

    const setPanelOpen = (open: boolean) => {
        panelContainer.setAttribute('data-open', String(open));
        panelContainer.setAttribute('role', 'dialog');
        panelContainer.setAttribute('aria-modal', 'false');
        panelContainer.setAttribute('aria-label', 'Customer chat');
        bubble.setAttribute('aria-label', open ? 'Close chat' : 'Open chat');
        bubble.setAttribute('aria-expanded', String(open));
        bubble.setAttribute('data-tip', open ? 'Close chat' : 'Chat with us');

        if (open) {
            setUnread(0);
            renderReact(true);
            syncSheetHeight();
            win.visualViewport?.addEventListener('resize', onViewportResize);
        } else {
            renderReact(false);
            panelContainer.style.height = '';
            win.visualViewport?.removeEventListener('resize', onViewportResize);
        }
    };

    const isOpen = () => panelContainer.getAttribute('data-open') === 'true';

    const unmount = () => {
        reactRoot?.unmount();
        host.remove();
    };

    renderReact(false);

    return { bubble, setPanelOpen, isOpen, unmount };
}

function injectStyles(shadow: ShadowRoot): void {
    if (!widgetStyles) {
        return;
    }

    const doc = shadow.ownerDocument;

    const propertyRules: string[] = [];
    const shadowCss = widgetStyles.replace(
        /@property\s+[^{]+\{[^}]*\}/g,
        (match) => {
            propertyRules.push(match);

            return '';
        },
    );

    if (
        propertyRules.length > 0 &&
        !doc.getElementById('orderpilot-property-hoist')
    ) {
        const style = doc.createElement('style');
        style.id = 'orderpilot-property-hoist';
        style.textContent = propertyRules.join('\n');
        doc.head.appendChild(style);
    }

    try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(shadowCss);
        shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet];
    } catch {
        const style = doc.createElement('style');
        style.textContent = shadowCss;
        shadow.append(style);
    }
}

const MESSAGE_CIRCLE_ICON = `
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
       stroke-linejoin="round" aria-hidden="true">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
  </svg>
`;
